import { toast, type ToastItem } from 'react-toastify'

/**
 * Ponte entre o Modo IA e as telas reais (ver docs/ai-user.md, "Como ela opera
 * as telas").
 *
 * Cada tela que o usuário de IA usa registra aqui um CONTROLADOR: funções com
 * nome de negócio que chamam os mesmos handlers da tela. A IA não clica em
 * coordenada nenhuma; ela pede "escreva este texto" e a tela faz o que faria
 * para uma pessoa, à vista do supervisor, com as validações de sempre.
 *
 * Registro global e não um contexto do React, como o `unsaved-work.ts`: as
 * telas estão espalhadas pela aplicação, e o Modo IA vive no layout.
 */

/** Controlador da tela da conversa (`atendimento-chat-page.tsx`). */
export interface ChatScreenController {
    chatId: number
    /** Escreve no campo de mensagem com ritmo de digitação. */
    typeDraft: (text: string, signal?: AbortSignal) => Promise<void>
    /** Anexa arquivos à próxima mensagem, como o clipe da tela (a guia, por exemplo). */
    attachFiles: (files: File[]) => void
    /** Limpa o texto e os anexos ainda não enviados. */
    clearDraft: () => void
    /** O que está no campo agora (o supervisor pode ter editado). */
    getDraft: () => string
    /** Envia pelo mesmo caminho do botão da tela. `true` se saiu. */
    send: () => Promise<boolean>
}

/** Controlador do quadro do funil (`pipeline-detail-page.tsx`). */
export interface PipelineScreenController {
    pipelineId: number
    /** Move o card pelo mesmo caminho do quadro e recarrega a tela. `true` se moveu. */
    moveDeal: (dealId: number, stageId: number) => Promise<boolean>
}

/** Item na tela de agendamento, como a IA o enxerga. Valores em centavos. */
export interface SchedulingItemSnapshot {
    /** Posição na tabela de itens: é por ela que se escolhe variação e se remove. */
    line: number
    catalogId: number
    name: string
    /** Tipo do item no catálogo (Análises Clínicas, Ultrassonografia, Consulta Especialidade Médica...). */
    type: string
    quantity: number
    unitPrice: number
    variation: { id: number; name: string } | null
    /** Variações ativas do item. Item com opções e sem variação escolhida pede a escolha ao contato. */
    variationOptions: Array<{ id: number; name: string; price: number }>
}

/** O que a vendedora enxerga na tela de agendamento, resumido. Valores em centavos. */
/** Tipos de atendimento que a IA orça: exame, consulta e procedimento NÃO cirúrgico. */
export type AiSchedulingServiceType = 'EXAM' | 'CONSULTATION' | 'PROCEDURE'

export interface SchedulingSnapshot {
    /** Um tipo por vez: a tela monta exame, consulta OU procedimento. */
    serviceType: string
    client: { id: number; name: string } | null
    /** Na consulta, no máximo um item (a consulta), com a variação escolhida. */
    items: SchedulingItemSnapshot[]
    /** Profissionais que atendem os itens. O total muda ao escolher um (`chooseProfessional`). */
    professionals: Array<{ id: number; name: string }>
    professional: { id: number; name: string } | null
    /**
     * Parceiros que fazem TODOS os itens, com o total de cada um para estes itens. `name` é o
     * nome fantasia (o que se diz ao contato); `legalName`, a razão social, que o contato
     * às vezes usa ("Instituto da Imagem" para MULTIMAGEM).
     */
    partners: Array<{ id: number; name: string; legalName: string | null; total: number }>
    /**
     * Os mesmos parceiros, por tipo de exame. Pedido com sangue e imagem não tem quem
     * faça tudo: cada tipo vai a um parceiro que faça todos os itens DAQUELE tipo, e
     * vira um atendimento próprio, com pagamento único (docs/ai-user.md).
     */
    partnersByType: Array<{
        type: string
        lines: number[]
        partners: Array<{ id: number; name: string; legalName: string | null; total: number }>
    }>
    partner: { id: number; name: string } | null
    /** O que será cobrado: com o parceiro escolhido e o cupom. */
    total: number
    coupon: { code: string; amount: number } | null
    /** A tela somaria este atendimento numa cobrança já aberta do paciente. */
    grouping: { billingId: number; count: number; amount: number } | null
    addressConfirmed: boolean
    /** Local de atendimento escolhido, em uma linha. `null` = ainda não escolhido. */
    address: string | null
    /** Endereços cadastrados do parceiro (ou profissional) escolhido, para `chooseAddress`. */
    addressOptions: Array<{ id: number; owner: 'partner' | 'professional'; label: string | null; line: string }>
    /**
     * Forma de pagamento escolhida: `later` = "Gerar cobrança depois" (nada é cobrado agora);
     * `onBillingScreen` = decidida na tela da cobrança (pagamento dividido). `null` = ainda não
     * escolhida; o envio exige a escolha, menos no "Adicionar outro".
     */
    payment:
        | { method: string; installments: number | null }
        | { later: true }
        | { onBillingScreen: true }
        | null
    /** Itens sem preço do executante: bloqueiam o envio (é cadastro, não venda). */
    missingPrices: string[]
    /** Exames lidos no pedido que não foram achados no catálogo. */
    unmatchedImports: string[]
    /** A tela terminou de carregar e calcular. */
    ready: boolean
}

/** Resultado de uma ação na tela: os avisos que ela mostrou e como ficou. */
export interface SchedulingActionResult {
    ok: boolean
    messages: string[]
    state: SchedulingSnapshot
}

/** Controlador da tela de agendamento em criação (`scheduling-form-page.tsx`). */
export interface SchedulingScreenController {
    getState: () => SchedulingSnapshot
    setClient: (clientId: number) => Promise<SchedulingActionResult>
    /** Troca o tipo do atendimento. A tela limpa os itens, como quando a pessoa troca. */
    setServiceType: (type: AiSchedulingServiceType) => Promise<SchedulingActionResult>
    searchCatalog: (
        query: string,
    ) => Promise<{
        ok: boolean
        messages: string[]
        /**
         * `variations`: as variações ativas do item (é por elas que se acha "com Doppler",
         * "sem contraste"). `partnerLinks`: vínculos com parceiros, somando todas as
         * filiais — serve para comparar itens duplicados, não para dizer quem atende aqui.
         */
        results: Array<{
            catalogId: number
            name: string
            /** Tipo de atendimento do item: é com ele que se abre a tela (`setServiceType`). */
            serviceType: AiSchedulingServiceType
            type: string
            partnerLinks: number
            variations: Array<{ name: string; partnerLinks: number }>
        }>
    }>
    addItem: (catalogId: number) => Promise<SchedulingActionResult>
    removeItem: (line: number) => Promise<SchedulingActionResult>
    chooseVariation: (line: number, variationId: number) => Promise<SchedulingActionResult>
    /** Lê o pedido médico em imagem pela mesma importação do botão da tela. */
    importOrderFromImage: (url: string) => Promise<SchedulingActionResult>
    choosePartner: (partnerId: number) => Promise<SchedulingActionResult>
    chooseProfessional: (professionalId: number) => Promise<SchedulingActionResult>
    applyCoupon: (code: string) => Promise<SchedulingActionResult>
    /** "Cobrar à parte": não soma na cobrança que o paciente já tem hoje. */
    chargeSeparately: () => Promise<SchedulingActionResult>
    /** Escolhe um endereço de `addressOptions` como local de atendimento. */
    chooseAddress: (addressId: number) => Promise<SchedulingActionResult>
    /** Forma de pagamento pelo mesmo gesto do bloco de pagamento da tela. */
    choosePayment: (method: string, installments: number | null) => Promise<SchedulingActionResult>
    /** "Gerar cobrança depois": agenda sem cobrança. */
    payLater: () => Promise<SchedulingActionResult>
    /** "Outra forma ou dividir o pagamento": a forma é decidida na tela da cobrança. */
    payOnBillingScreen: () => Promise<SchedulingActionResult>
    /**
     * Envia o agendamento pelo mesmo caminho do botão "Agendar" (ou "Adicionar outro", com
     * `addAnother`), no funil informado. Sem "Adicionar outro" a tela navega e o controlador
     * sai do ar: o resultado traz o que foi criado e para onde a tela foi.
     */
    submit: (pipelineId: number | null, addAnother: boolean) => Promise<SchedulingSubmitResult>
}

/** O que o envio criou. Valores em centavos. */
export interface SchedulingSubmitResult {
    ok: boolean
    messages: string[]
    created?: { kind: 'EXAM' | 'CONSULTATION' | 'PROCEDURE'; id: number }
    billing?: {
        id: number
        amount: number
        paymentMethod: string | null
        installments: number | null
        /** Link de pagamento (checkout), o mesmo que a tela mostra. */
        checkoutUrl: string | null
        /** Pix copia e cola, quando a forma é Pix. */
        pixCopyPaste: string | null
    }
    /** "Gerar cobrança depois": agendado sem cobrança. */
    billingDeferred?: boolean
    /**
     * A tela foi para a cobrança, onde a forma é escolhida (último atendimento de um grupo,
     * ou "dividir o pagamento"). Siga com as ações `billing_*`.
     */
    nextScreen?: 'billing'
    /** "Adicionar outro": a tela ficou aberta para o próximo atendimento, que soma nesta cobrança. */
    addedToGroup?: boolean
}

/** Tela de edição da cobrança (`billings-form-page.tsx`), como a IA a enxerga. Valores em centavos. */
export interface BillingSnapshot {
    billingId: number
    status: string | null
    /** O que o contato paga no total (forma principal + partes). */
    total: number
    /** Forma principal: é ela que parcela no cartão. */
    principal: { method: string | null; installments: number | null; amount: number }
    /** Partes em outra forma (pagamento dividido). */
    parts: Array<{ method: string; amount: number }>
    /** Formas que a empresa aceita nesta cobrança. */
    availableMethods: string[]
    ready: boolean
}

export interface BillingActionResult {
    ok: boolean
    messages: string[]
    state: BillingSnapshot
}

/** O que ficou gravado depois de salvar a cobrança. */
export interface BillingSaveResult {
    ok: boolean
    messages: string[]
    /** Uma linha por forma: a principal e cada parte, com o link e o Pix de cada uma. */
    payments?: Array<{
        billingId: number
        method: string | null
        installments: number | null
        amount: number
        checkoutUrl: string | null
        pixCopyPaste: string | null
    }>
}

/** Controlador da tela de edição da cobrança (`billings-form-page.tsx`, vinda do agendamento). */
export interface BillingScreenController {
    billingId: number
    getState: () => BillingSnapshot
    /** Forma principal e parcelas (só no cartão de crédito). */
    setPayment: (method: string, installments: number | null) => Promise<BillingActionResult>
    /** Partes em outra forma; a principal fica com o resto do total. Lista vazia desfaz. */
    setParts: (parts: Array<{ method: string; amount: number }>) => Promise<BillingActionResult>
    /** Salva pelo mesmo botão "Finalizar". A tela navega e o controlador sai do ar. */
    save: () => Promise<BillingSaveResult>
}

type ScreenControllers = {
    chat: ChatScreenController
    pipeline: PipelineScreenController
    scheduling: SchedulingScreenController
    billing: BillingScreenController
}

type ScreenName = keyof ScreenControllers

const controllers = new Map<ScreenName, ScreenControllers[ScreenName]>()
const waiters = new Set<() => void>()

/** Registra o controlador da tela. Devolve a função que o remove — use no `useEffect`. */
export function registerScreenController<K extends ScreenName>(
    name: K,
    controller: ScreenControllers[K],
): () => void {
    controllers.set(name, controller)
    waiters.forEach(notify => notify())
    return () => {
        // Só remove se ainda for o mesmo: numa remontagem o novo registra antes de
        // o antigo limpar (mesma guarda do `unsaved-work.ts`).
        if (controllers.get(name) === controller) controllers.delete(name)
    }
}

export function getScreenController<K extends ScreenName>(
    name: K,
): ScreenControllers[K] | undefined {
    return controllers.get(name) as ScreenControllers[K] | undefined
}

/**
 * Espera a tela registrar um controlador que satisfaça `accept` (ex.: a conversa
 * certa já carregada). Devolve `undefined` se o prazo vencer ou se for abortado.
 */
export function waitForScreenController<K extends ScreenName>(
    name: K,
    accept: (controller: ScreenControllers[K]) => boolean,
    timeoutMs: number,
    signal?: AbortSignal,
): Promise<ScreenControllers[K] | undefined> {
    return new Promise(resolve => {
        let done = false
        const finish = (value: ScreenControllers[K] | undefined) => {
            if (done) return
            done = true
            waiters.delete(check)
            clearTimeout(timer)
            signal?.removeEventListener('abort', onAbort)
            resolve(value)
        }
        const check = () => {
            const current = getScreenController(name)
            if (current && accept(current)) finish(current)
        }
        const onAbort = () => finish(undefined)
        const timer = setTimeout(() => finish(undefined), timeoutMs)
        signal?.addEventListener('abort', onAbort)
        waiters.add(check)
        check()
    })
}

/** Fatos que as telas anunciam ao Modo IA. */
export type ScreenEvent = { type: 'chat:sent'; chatId: number }

const eventListeners = new Set<(event: ScreenEvent) => void>()

/**
 * A tela anuncia o que aconteceu nela. Hoje: mensagem enviada na conversa —
 * é como o Modo IA sabe que o supervisor aprovou pelo botão da própria tela,
 * e não pelo painel.
 */
export function emitScreenEvent(event: ScreenEvent): void {
    eventListeners.forEach(listener => listener(event))
}

export function onScreenEvent(listener: (event: ScreenEvent) => void): () => void {
    eventListeners.add(listener)
    return () => {
        eventListeners.delete(listener)
    }
}

/**
 * Roda `action` e devolve, junto do resultado, os avisos (toasts) que a tela mostrou
 * enquanto isso. É como a IA fica sabendo do erro que a vendedora leria na tela, sem
 * duplicar a validação.
 */
export async function collectToasts<T>(action: () => Promise<T>): Promise<{ result: T; toasts: string[] }> {
    const toasts: string[] = []
    const unsubscribe = toast.onChange((payload: ToastItem) => {
        if (payload.status !== 'added' || typeof payload.content !== 'string') return
        toasts.push(payload.type === 'error' || payload.type === 'warning' ? `Erro: ${payload.content}` : payload.content)
    })
    try {
        const result = await action()
        return { result, toasts }
    } finally {
        unsubscribe()
    }
}

/** Intervalo entre os pedaços digitados no campo. */
const TYPING_TICK_MS = 30
/** A digitação inteira não passa disto, por mais longo que seja o texto. */
const TYPING_MAX_MS = 3500

/**
 * Escreve `text` aos poucos, como uma pessoa digitando, chamando `setText` com o
 * trecho já digitado. Serve para o supervisor acompanhar a resposta nascendo no
 * campo da conversa; o ritmo acelera em texto longo para não passar de
 * `TYPING_MAX_MS`.
 */
export async function typeProgressively(
    text: string,
    setText: (value: string) => void,
    signal?: AbortSignal,
): Promise<void> {
    const ticks = Math.max(1, Math.floor(TYPING_MAX_MS / TYPING_TICK_MS))
    const step = Math.max(1, Math.ceil(text.length / ticks))
    for (let end = step; end < text.length; end += step) {
        if (signal?.aborted) return
        setText(text.slice(0, end))
        await new Promise(resolve => setTimeout(resolve, TYPING_TICK_MS))
    }
    if (!signal?.aborted) setText(text)
}
